import { useEffect, useRef, useState } from 'react';
import { KeyRound, Megaphone, Users } from 'lucide-react';

import { Badge } from './Badge';
import { UserMenu } from './UserMenu';
import { useAuth } from '../providers/auth-provider';

import type { TabId } from '../hooks/use-navigation';

export interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  taskBadgeCount?: number;
  className?: string;
}

export function TabBar({ activeTab, onTabChange, taskBadgeCount, className = '' }: TabBarProps) {
  const { user } = useAuth();
  const canManageUsers = user?.role === 'owner' || user?.role === 'admin';

  const conversationsRef = useRef<HTMLButtonElement>(null);
  const tasksRef = useRef<HTMLButtonElement>(null);
  const credentialsRef = useRef<HTMLButtonElement>(null);
  const usersRef = useRef<HTMLButtonElement>(null);
  const changelogRef = useRef<HTMLButtonElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const refMap: Record<TabId, React.RefObject<HTMLButtonElement | null>> = {
    conversations: conversationsRef,
    tasks: tasksRef,
    credentials: credentialsRef,
    users: usersRef,
    changelog: changelogRef,
  };

  // Build visible tabs array based on role
  const tabs: TabId[] = canManageUsers
    ? ['conversations', 'tasks', 'credentials', 'users', 'changelog']
    : ['conversations', 'tasks', 'credentials', 'changelog'];

  useEffect(() => {
    const el = refMap[activeTab].current;
    if (el) {
      setIndicatorStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeTab]);

  const baseClass =
    'min-h-[44px] px-4 flex items-center focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 focus-visible:outline-none transition-colors duration-200 hover:bg-sd-surface-elevated/50 motion-reduce:transition-none';
  const activeClass = `${baseClass} text-sd-text`;
  const inactiveClass = `${baseClass} text-sd-text-secondary`;

  const handleTabChange = (tab: TabId) => {
    onTabChange(tab);
    const event = new CustomEvent('navigation', {
      bubbles: true,
      composed: true,
      detail: { tab },
    });
    refMap[tab].current?.dispatchEvent(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent, tab: TabId) => {
    const currentIndex = tabs.indexOf(tab);
    let newIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else {
      return;
    }

    const newTab = tabs[newIndex]!;
    onTabChange(newTab);

    refMap[newTab]?.current?.focus();

    const navEvent = new CustomEvent('navigation', {
      bubbles: true,
      composed: true,
      detail: { tab: newTab },
    });
    refMap[newTab]?.current?.dispatchEvent(navEvent);
  };

  return (
    <div className={`sticky top-0 bg-sd-background z-30 flex items-center border-b border-sd-border/30 shadow-[0_1px_3px_rgba(0,0,0,0.06)] will-change-transform [backface-visibility:hidden] ${className}`.trim()}>
      <div role="tablist" className="relative flex">
        <button
          ref={conversationsRef}
          id="tab-conversations"
          role="tab"
          aria-selected={activeTab === 'conversations'}
          aria-controls="tabpanel-conversations"
          tabIndex={activeTab === 'conversations' ? 0 : -1}
          className={activeTab === 'conversations' ? activeClass : inactiveClass}
          onClick={() => handleTabChange('conversations')}
          onKeyDown={(e) => handleKeyDown(e, 'conversations')}
        >
          Conversations
        </button>
        <button
          ref={tasksRef}
          id="tab-tasks"
          role="tab"
          aria-selected={activeTab === 'tasks'}
          aria-controls="tabpanel-tasks"
          tabIndex={activeTab === 'tasks' ? 0 : -1}
          className={activeTab === 'tasks' ? activeClass : inactiveClass}
          onClick={() => handleTabChange('tasks')}
          onKeyDown={(e) => handleKeyDown(e, 'tasks')}
        >
          Tasks
          {taskBadgeCount !== undefined && taskBadgeCount > 0 && (
            <span className="ml-sd-1 relative -top-px">
              <Badge variant="awaiting-input" size="sm">
                {taskBadgeCount}
              </Badge>
            </span>
          )}
        </button>
        <button
          ref={credentialsRef}
          id="tab-credentials"
          role="tab"
          aria-selected={activeTab === 'credentials'}
          aria-controls="tabpanel-credentials"
          tabIndex={activeTab === 'credentials' ? 0 : -1}
          className={activeTab === 'credentials' ? activeClass : inactiveClass}
          onClick={() => handleTabChange('credentials')}
          onKeyDown={(e) => handleKeyDown(e, 'credentials')}
        >
          <KeyRound size={14} className="mr-sd-1" />
          Credentials
        </button>
        {canManageUsers && (
          <button
            ref={usersRef}
            id="tab-users"
            role="tab"
            aria-selected={activeTab === 'users'}
            aria-controls="tabpanel-users"
            tabIndex={activeTab === 'users' ? 0 : -1}
            className={activeTab === 'users' ? activeClass : inactiveClass}
            onClick={() => handleTabChange('users')}
            onKeyDown={(e) => handleKeyDown(e, 'users')}
          >
            <Users size={14} className="mr-sd-1" />
            Team
          </button>
        )}
        <button
          ref={changelogRef}
          id="tab-changelog"
          role="tab"
          aria-selected={activeTab === 'changelog'}
          aria-controls="tabpanel-changelog"
          tabIndex={activeTab === 'changelog' ? 0 : -1}
          className={activeTab === 'changelog' ? activeClass : inactiveClass}
          onClick={() => handleTabChange('changelog')}
          onKeyDown={(e) => handleKeyDown(e, 'changelog')}
        >
          <Megaphone size={14} className="mr-sd-1" />
          What&apos;s New
        </button>
        <span
          data-testid="tab-indicator"
          className="absolute bottom-0 h-[2px] bg-sd-warning rounded-full transition-[left,width] duration-300 ease-out motion-reduce:transition-none"
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        />
      </div>
      <div className="ml-auto flex items-center pr-sd-2">
        <UserMenu />
      </div>
    </div>
  );
}
